"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  UseSearchBoxProps,
  useInstantSearch,
  useSearchBox,
} from "react-instantsearch";

import { css } from "@/../styled-system/css";
import LoaderCircle from "@/assets/svg/loader-circle.svg";
import Search from "@/assets/svg/search.svg";
import Cross from "@/assets/svg/x.svg";
import { useSearchStore } from "@/providers/search-store-provider";

import Input from "../ui/Input";
import { FormControl, FormField, FormItem, ReactForm } from "../ui/ReactForm";

const formContainerClass = css({
  position: "relative",
  display: "flex",
  width: "100%",
  height: "56px",
  py: "4",
  pl: "4",
  pr: "2",
  borderWidth: "2px",
  borderColor: "black",
  rounded: "full",
  alignItems: "center",
  gap: "4",
  _focusWithin: {
    color: "brandTeal",
    "& .cross-icon": {
      stroke: "brandTeal",
    },
    "& .search-icon": {
      fill: "brandTeal",
    },
  },
  md: {
    mr: "0",
    mb: "0",
  },
});

const searchInputClass = css({
  width: "100%",
  margin: "0 auto",
  outline: "none",
  rounded: "inherit",
  lineHeight: "none",
  height: "100%",
});

const searchBarButtonClass = css({
  minWidth: "40px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  rounded: "full",
  cursor: "pointer",
  transition: "all 0.15s ease-in-out",
  _hover: {
    backgroundColor: "brandLightGray/15",
  },
});

const spinnerContainerClass = css({
  minWidth: "40px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export default function NewSearchBar(props: UseSearchBoxProps) {
  const {
    query: storeQuery,
    setQuery,
    clearQuery,
  } = useSearchStore((state) => ({
    query: state.query,
    setQuery: state.setQuery,
    clearQuery: state.clearQuery,
  }));
  const { refine } = useSearchBox(props);
  const { status } = useInstantSearch();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isSearchStalled = status === "stalled";
  const form = useForm();

  useEffect(() => {
    refine(storeQuery);
  }, [storeQuery, refine]);

  const onSubmitSearch = () => {
    setIsLoading(true);
    const queryParams = `?query=${encodeURIComponent(storeQuery)}`;
    if (inputRef.current) {
      inputRef.current.blur();
    }
    clearQuery();
    router.push(`/catalog${queryParams}`);
    setIsLoading(false);
  };

  return (
    <ReactForm {...form}>
      <form
        action=""
        role="search"
        noValidate
        className={formContainerClass}
        onSubmit={form.handleSubmit(onSubmitSearch)}
        onReset={(event) => {
          event.preventDefault();
          event.stopPropagation();
          clearQuery();
        }}
      >
        <FormField
          name="search"
          control={form.control}
          defaultValue={storeQuery}
          render={({ field }) => (
            <FormItem css={{ width: "100%", height: "100%" }}>
              <FormControl>
                <Input
                  {...field}
                  autoComplete="off"
                  ref={inputRef}
                  disabled={isLoading}
                  autoCorrect="off"
                  autoCapitalize="off"
                  placeholder="Search Solecit"
                  spellCheck={false}
                  maxLength={512}
                  type="search"
                  value={storeQuery}
                  onChange={(event) => setQuery(event.currentTarget.value)}
                  className={searchInputClass}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className={css({ display: "flex", gap: "1" })}>
          <button
            type="reset"
            aria-label="Clear Search Query"
            hidden={storeQuery.length === 0 || isSearchStalled}
            className={searchBarButtonClass}
          >
            <Cross width={24} stroke="#686664" className="cross-icon" />
          </button>
          <div className={spinnerContainerClass} hidden={!isSearchStalled}>
            <LoaderCircle width={24} className={css({ animation: "spin" })} />
          </div>
          <button
            type="submit"
            aria-label="Submit Search Query"
            className={searchBarButtonClass}
          >
            <Search width={24} fill="#686664" className="search-icon" />
          </button>
        </div>
      </form>
    </ReactForm>
  );
}
